import React, { useMemo } from "react";
import "~/assets/styles/old-tailwind.css";
import {
  TrackingProvider,
  initializeAnalytics,
} from "@local-logic/core/tracking";
import { mapUtils } from "@local-logic/core/utils";

import ResizeWrapper from "~/components/ResizeWrapper";
import { ValidateOptions } from "~/components/ValidateOptions";
import { LocalLogicProvider } from "~/context/clientProvider";
import { useStateManagerContext } from "~/context/stateManager";
import useIsMobile from "~/hooks/useIsMobile";
import { ProductNames } from "~/types";
import { ANALYTICS_SETTINGS } from "~/constants/analytics";
import type { NeighborhoodHeroProps } from "./types";

import { NeighborhoodHeroProvider } from "./context";
import { Content } from "./components/Content";
import { schema } from "./schema";

initializeAnalytics(ANALYTICS_SETTINGS.neighborhoodWrap.key);

const NeighborhoodHero = () => {
  const { sdkState } = useStateManagerContext<NeighborhoodHeroProps>();
  const isMobile = useIsMobile();
  const locale = sdkState.globalOptions?.locale || "en";

  const clientId = useMemo(() => {
    const clientKey = sdkState.apiKey?.split(".") ?? "";

    return clientKey[0].slice(3);
  }, [sdkState.apiKey]);

  const location = useMemo(() => {
    if (sdkState.options?.geographyId) {
      return {
        ...mapUtils.decodeGeogId(sdkState.options?.geographyId),
        geographyId: sdkState.options?.geographyId,
      };
    }

    return {
      lat: sdkState.options?.lat,
      lng: sdkState.options?.lng,
    };
  }, [
    sdkState.options?.lat,
    sdkState.options?.lng,
    sdkState.options?.geographyId,
  ]);

  return (
    <ResizeWrapper>
      <ValidateOptions
        sdk="Neighborhood Hero"
        schema={schema}
        options={sdkState.options!}
      >
        <LocalLogicProvider
          environment={sdkState.globalOptions?.environment?.api}
          apiKey={sdkState.apiKey}
          appearance={sdkState.globalOptions?.appearance}
          locale={sdkState.globalOptions?.locale}
          product={ProductNames.NEIGHBORHOOD_HERO}
        >
          <TrackingProvider
            trackingPipelineSettings={{
              apiKey: sdkState.apiKey || "",
            }}
            analyticsSettings={ANALYTICS_SETTINGS.neighborhoodWrap}
            meta={{
              clientId,
              productName: ProductNames.NEIGHBORHOOD_HERO,
              productVersion: FULL_APP_VERSION,
            }}
            attributes={{
              location,
              host_url: document.referrer,
              locale,
              isMobile,
            }}
            flags={{
              isDebugMode:
                import.meta.env.VITE_TRACKING_PIPELINE_DEBUG === "true",
            }}
          >
            <NeighborhoodHeroProvider>
              <Content />
            </NeighborhoodHeroProvider>
          </TrackingProvider>
        </LocalLogicProvider>
      </ValidateOptions>
    </ResizeWrapper>
  );
};

export default NeighborhoodHero;
